import { useCallback, useEffect, useState } from 'react';
import mapApi from '~/apis/map';
import { handleApiError } from '~/utils/helper';
import i18n from '~/utils/i18n';
import type { IMapStatisticDatum } from '~/utils/interface/map';

export function extractStatisticPayloadArray(payload: unknown): unknown[] {
  if (payload == null) return [];
  if (Array.isArray(payload)) return payload;
  if (typeof payload !== 'object') return [];

  const o = payload as Record<string, unknown>;

  if (Array.isArray(o.data)) return o.data;

  if (Array.isArray(o.result)) return o.result;

  if (o.result && typeof o.result === 'object') {
    const result = o.result as Record<string, unknown>;
    if (Array.isArray(result.data)) return result.data;
  }

  return [];
}

function parseFiniteNumber(raw: unknown): number | null {
  if (raw === null || raw === undefined || raw === '') return null;
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

function parseStatisticTotal(raw: unknown): number {
  const n = parseFiniteNumber(raw);
  return n != null && n >= 0 ? n : 0;
}

function parseStatisticId(raw: unknown): number {
  const n = parseFiniteNumber(raw);
  return n != null ? Math.round(n) : 0;
}

export function normalizeStatisticDatumRow(raw: Record<string, unknown>): IMapStatisticDatum | null {
  const lat = parseFiniteNumber(raw.lat);
  const lng = parseFiniteNumber(raw.lng);
  if (lat == null || lng == null) {
    return null;
  }

  const total = parseStatisticTotal(raw.total);
  const id = parseStatisticId(raw.id);
  const code = typeof raw.code === 'string' ? raw.code : raw.code != null ? String(raw.code) : '';
  const name = typeof raw.name === 'string' ? raw.name : raw.name != null ? String(raw.name) : '';

  return {
    id,
    code,
    name,
    lat,
    lng,
    total,
  };
}

export function normalizeResponseData(rows: unknown): IMapStatisticDatum[] {
  if (!Array.isArray(rows)) return [];
  const out: IMapStatisticDatum[] = [];
  for (const row of rows) {
    if (row === null || typeof row !== 'object') continue;
    const normalized = normalizeStatisticDatumRow(row as Record<string, unknown>);
    if (normalized) out.push(normalized);
  }
  return out;
}

export function useMapStatistic(username: string | null | undefined) {
  const [statisticData, setStatisticData] = useState<IMapStatisticDatum[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const fetchStatistic = useCallback(async () => {
    const trimmed = typeof username === 'string' ? username.trim() : '';
    if (!trimmed) {
      setStatisticData([]);
      setErrorMessage(null);
      return;
    }

    setIsLoading(true);
    setErrorMessage(null);
    try {
      const res = await mapApi.getMapStatistic(trimmed);
      const axiosBody = res?.data as unknown;
      const rows = extractStatisticPayloadArray(axiosBody);
      setStatisticData(normalizeResponseData(rows));
    } catch (err) {
      setStatisticData([]);
      setErrorMessage(i18n.t('errors.loadStatistic', { ns: 'admin/map' }));
      handleApiError(err);
    } finally {
      setIsLoading(false);
    }
  }, [username]);

  useEffect(() => {
    void fetchStatistic();
  }, [fetchStatistic]);

  return {
    statisticData,
    isLoading,
    errorMessage,
    refetch: fetchStatistic,
  };
}
